import React from 'react';
import { Theme, } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Drawer from '@mui/material/Drawer';
import {
  List,
  ListItem,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { isDevMode } from '../../electron-modules';
import { SlimScrollbar } from '../../components';
import { useTranslation } from "react-i18next";
import { headerHeight } from '../../app';
import clsx from 'clsx';
import {
  BoudingBoxDebug,
  ClassificationList,
  ModelDistance,
  GPUPickerDebug,
  PointCloudDistance,
  PointsRenderDebug,
  TagDrawDistance,
  PointCloudShape,
  AdaptivePointSize,
  PointCloudScale,
  PointCloudSize,
  IntensityRange,
  HeightRange,
  ColorMapPicker,
  PointCloudCount,
  PointCloudOpacity,
  NavigationType,
  PointCloudColor,
  SectionTitle,
  SectionDescription,
  AerialBasemapPicker
} from './components';
import { useSelector } from 'react-redux';
import { selectOrbitMode } from '../../redux/camera-slice';

export const settingsDrawerWidth = 280;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      display: "none"
    },
    drawer: {
      width: 0,
      "z-index": `${theme.zIndex.appBar - 1} !important`,
    },
    drawerPaper: {
      width: settingsDrawerWidth,
      paddingTop: `${headerHeight}px`,
      height: `calc(100% - ${headerHeight}px)`,
    },
    list: {
      width: '100%',
      maxWidth: 360,
      backgroundColor: theme.palette.background.paper,
    },
    title: {
      flex: 1,
      margin: theme.spacing(0)
    },
    boldTitleTypography: {
      fontWeight: 'bold',
      fontSize: '1em'
    }
  }),
);

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const SettingsDrawer = (props: SettingsDrawerProps) => {
  const {open, onClose} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const isOrbitMode = useSelector(selectOrbitMode);

  return (
    <Drawer
      open={open}
      anchor="right"
      keepMounted
      disableEnforceFocus
      transitionDuration={0}
      className={classes.drawer}
      classes={{
        paper: classes.drawerPaper,
      }}
      slotProps={{
        backdrop: {
          classes: {
            root: classes.backdrop
          }
        }
      }}
    >
      <SlimScrollbar>

        <List dense className={classes.list}>
          <ListItem>
            <Typography
              className={clsx(classes.boldTitleTypography, classes.title)}
              gutterBottom
            >
              {t('user-settings.project_settings')}
            </Typography>

            {/* Close button */}
            <IconButton aria-label="close" onClick={onClose} size="large">
              <CloseIcon fontSize="small"/>
            </IconButton>
          </ListItem>
        </List>

        {/* Developer Controls */}
        {isDevMode && (<React.Fragment>
          <List dense className={classes.list}>
            <SectionTitle text={t('user-settings.developer-debug')}/>
            <GPUPickerDebug/>
            <PointsRenderDebug/>
            <BoudingBoxDebug/>
          </List>

          <Divider variant="middle" />
        </React.Fragment>)}

        {/* Point Cloud Controls */}
        <List dense className={classes.list}>
          <SectionTitle text={t("user-settings.point-cloud-controls")}/>
          <PointCloudColor/>
          <ColorMapPicker/>
          <NavigationType/>
          <PointCloudOpacity/>
          <PointCloudCount/>
          <HeightRange/>
          <IntensityRange/>
          <PointCloudShape/>
        </List>

        <Divider variant="middle" />

        {/* Point Cloud Size */}
        <List dense className={classes.list}>
          <SectionTitle text={t('user-settings.point-cloud-sizing')}/>
          <PointCloudScale/>
          <PointCloudSize/>
          <AdaptivePointSize/>
        </List>

        <Divider variant="middle" />

        {/* Aerial Map Controls */}
        <List dense className={classes.list}>
          <SectionTitle text={t('user-settings.aerial_map_controls')}/>
          <AerialBasemapPicker/>
        </List>

        <Divider variant="middle" />

        {/* Draw distances */}
        <List dense className={classes.list}>
          <SectionTitle text={t('user-settings.draw_distances')}/>

          {isOrbitMode && <SectionDescription
            text={t('user-settings.draw_distances_description')}
          />}

          {!isOrbitMode && <React.Fragment>
            <PointCloudDistance/>
            <TagDrawDistance/>
            <ModelDistance/>
          </React.Fragment>}
        </List>

        <Divider variant="middle" />

        {/* Classifications */}
        <ClassificationList/>

      </SlimScrollbar>
    </Drawer>
  );
};